import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { VACCINATION_STATUS, getVaccinationStatus as getVaccinationStatusPure } from '../utils/vaccinationStatus';

// Re-export for backward compatibility
export { VACCINATION_STATUS };

const usePetRegistrations = (filters) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(filters !== null);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const prevFiltersRef = useRef(null);
  const isMountedRef = useRef(true);
  const enabled = filters !== null;

  // Manual refetch function
  const refetch = useCallback(() => {
    prevFiltersRef.current = null; // Reset to force refetch
    setRefreshKey(prev => prev + 1);
  }, []);

  const district = filters?.district || '';
  const tahsil = filters?.tahsil || '';
  const uc = filters?.uc || '';
  const startDate = filters?.startDate || '';
  const endDate = filters?.endDate || '';
  const status = filters?.status || '';

  // Wrapper around the shared pure function — stable reference for processRecords dep
  const getVaccinationStatus = useCallback((pet) => getVaccinationStatusPure(pet), []);

  // Process and filter records
  const processRecords = useCallback((querySnapshot, startDate, endDate, districtFilter, tahsilFilter, ucFilter, statusFilter) => {
    let records = [];

    querySnapshot.forEach((doc) => {
      const docData = doc.data();
      const vaxStatus = getVaccinationStatus(docData);

      records.push({
        id: doc.id,
        // Pet info
        registrationId: docData.registrationId || '',
        petName: docData.petName || 'Unknown',
        breed: docData.breed || '',
        age: docData.age || '',
        bodyCoatColour: docData.bodyCoatColour || '',
        sex: docData.sex || '',
        idMark: docData.idMark || '',
        imageUrl: docData.imageUrl || '',
        // Owner info
        ownerName: docData.ownerName || '',
        ownerPhone: docData.ownerPhone || '',
        ownerAddress: docData.ownerAddress || '',
        ownerCNIC: docData.ownerCNIC || '',
        ownerId: docData.ownerId || '',
        // Location
        district: docData.district || 'Vehari',
        division: docData.division || '',
        tahsil: docData.tehsil || '',
        ucId: docData.ucMcNumber || docData.uc || '',
        uc: docData.uc || docData.ucMcNumber || '',
        // Coordinates
        latitude: docData.coordinates?.lat,
        longitude: docData.coordinates?.lng,
        coordinates: docData.coordinates || null,
        // Hospital/Vet info
        hospitalType: docData.hospitalType || '',
        // Status
        status: docData.status || 'Registered',
        vaccinationStatus: vaxStatus,
        vaccinationDate: docData.vaccinationDate,
        // Metadata
        docId: docData.docId || '',
        createdAt: docData.createdAt,
        // Handle both Firestore Timestamp and regular Date objects
        date: docData.createdAt?.toDate?.() || (docData.createdAt instanceof Date ? docData.createdAt : (docData.createdAt ? new Date(docData.createdAt) : null)),
        // Data source identifier
        dataSource: 'petRegistration'
      });
    });

    // Apply filters
    if (districtFilter) {
      records = records.filter(r => r.district === districtFilter);
    }
    if (tahsilFilter) {
      records = records.filter(r => r.tahsil === tahsilFilter);
    }
    if (ucFilter) {
      const normalizedFilter = ucFilter.toLowerCase().trim().replace(/\s+/g, '');
      records = records.filter(r => {
        const ucValue = (r.uc || '').toLowerCase().trim().replace(/\s+/g, '');
        const ucIdValue = (r.ucId || '').toLowerCase().trim().replace(/\s+/g, '');
        return ucValue.includes(normalizedFilter) ||
          normalizedFilter.includes(ucValue) ||
          ucIdValue.includes(normalizedFilter) ||
          normalizedFilter.includes(ucIdValue);
      });
    }
    if (statusFilter) {
      // Filter using computed vaccinationStatus (which already accounts for vaccinationDate expiry).
      // vaccinationStatus is set at record-processing time via getVaccinationStatus, so a direct
      // equality check against the VACCINATION_STATUS constants is correct and date-aware.
      records = records.filter(r => {
        if (statusFilter === 'Registered') {
          return r.vaccinationStatus === VACCINATION_STATUS.REGISTERED;
        }
        if (statusFilter === 'First Shot') {
          return r.vaccinationStatus === VACCINATION_STATUS.FIRST_SHOT;
        }
        if (statusFilter === 'First Shot Expired') {
          return r.vaccinationStatus === VACCINATION_STATUS.FIRST_EXPIRED;
        }
        if (statusFilter === 'Vaccinated') {
          return r.vaccinationStatus === VACCINATION_STATUS.VACCINATED;
        }
        if (statusFilter === 'Vaccination Expired') {
          return r.vaccinationStatus === VACCINATION_STATUS.VACCINATION_EXPIRED;
        }
        return false;
      });
    }

    // Client-side date filtering
    if (startDate || endDate) {
      records = records.filter(record => {
        if (!record.date) return false;
        if (startDate && endDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return record.date >= start && record.date <= end;
        } else if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          return record.date >= start;
        } else if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return record.date <= end;
        }
        return true;
      });
    }

    // Sort by date (newest first)
    records.sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return b.date - a.date;
    });

    return records;
  }, [getVaccinationStatus]);

  useEffect(() => {
    isMountedRef.current = true;

    // Skip fetch when disabled (filters === null)
    if (!enabled) {
      setData([]);
      setLoading(false);
      return;
    }

    const filterKey = JSON.stringify({ district, tahsil, uc, startDate, endDate, status });

    if (prevFiltersRef.current === filterKey) {
      return;
    }
    prevFiltersRef.current = filterKey;

    const fetchData = async () => {
      if (isMountedRef.current) {
        setLoading(true);
      }

      try {
        // Fetch from PetRegistrations collection (all filtering done client-side in processRecords)
        const q = query(
          collection(db, 'PetRegistrations'),
          orderBy('createdAt', 'desc'),
          limit(1000)
        );

        const querySnapshot = await getDocs(q);
        const records = processRecords(querySnapshot, startDate, endDate, district, tahsil, uc, status);

        if (isMountedRef.current) {
          setData(records);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching pet registrations:', err);
        if (isMountedRef.current) {
          setError(err.message);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMountedRef.current = false;
    };
  }, [district, tahsil, uc, startDate, endDate, status, processRecords, refreshKey, enabled]);

  return { data, loading, error, getVaccinationStatus, refetch };
};

export default usePetRegistrations;
